import { createSelector } from '@reduxjs/toolkit';
import { Team } from 'api/models';
import { RootState } from 'data';
import { TemplateId } from '../api';
import {
  ProjectTemplateTask,
  ProjectTemplateProjectPhase,
  ProjectTemplate,
  ProjectTemplateSpaceSetupStage,
  ProjectTemplateSpaceData,
} from './types';

export const selectAllTemplateTask =
  () =>
  ({ templates }: RootState): { [key: string]: ProjectTemplateTask } =>
    templates.project.data[templates.project.activeId].tasks;

export const selectAllTemplateProjectPhase =
  () =>
  ({ templates }: RootState): { [key: string]: ProjectTemplateProjectPhase } =>
    templates.project.data[templates.project.activeId].projectPhases;

export const selectTemplateTask =
  (id: number) =>
  ({ templates }: RootState): ProjectTemplateTask =>
    templates.project.data[templates.project.activeId].tasks[id];

export const selectTemplateProjectPhase =
  (id: number) =>
  ({ templates }: RootState): ProjectTemplateProjectPhase =>
    templates.project.data[templates.project.activeId].projectPhases[id];

export const getProjectTemplateById =
  (id: number) =>
  ({ templates }: RootState): ProjectTemplate =>
    templates.project.data[id].template;

export const getProjectTemplatePhaseIds =
  (id: TemplateId) =>
  ({ templates }: RootState): number[] =>
    templates.project.data[id].template.templateContent.projectPhases;

export const selectActiveTemplateId = createSelector(
  (state: RootState) => state.templates.project.activeId,
  (activeId: TemplateId): TemplateId => activeId,
);

export const selectActiveId =
  () =>
  ({ templates }: RootState): TemplateId =>
    templates.project.activeId;

export const selectActiveSpaceId =
  () =>
  ({ templates }: RootState): number | null =>
    templates.project.activeSpaceId;

export const selectActiveSpaceTabId =
  () =>
  ({ templates }: RootState): number | null =>
    templates.project.activeSpaceTabId;

export const hasSetupSpacesInTemplate =
  () =>
  ({ templates }: RootState): ProjectTemplateSpaceSetupStage => {
    return templates.project.data[templates.project.activeId].spaceSetup;
  };

export const selectParentName =
  (id: number, data?: ProjectTemplateSpaceData) =>
  ({ templates }: RootState): undefined | string => {
    const { projectPhases, tasks, spaceList } =
      templates.project.data[templates.project.activeId];

    if (projectPhases[id]) {
      return undefined; // no parent for a phase
    }

    if (tasks[id]) {
      const { parentTask, projectPhase } = tasks[id];

      if (parentTask) {
        return tasks[parentTask.taskId].taskName;
      }

      return projectPhases[projectPhase.projectPhaseId].projectPhaseName;
    }

    if (spaceList[id]) {
      return undefined;
    }

    if (data && data.parentId !== 0) {
      return spaceList[data!.parentId].spaceName;
    }

    return undefined;
  };

export const selectSpaceName =
  (id: number) =>
  ({ templates }: RootState): undefined | string => {
    const { spaceList } = templates.project.data[templates.project.activeId];

    if (!spaceList[id]) {
      return undefined;
    }

    return spaceList[id].spaceName;
  };

export const selectSpaceTabName =
  (spaceId: number, spaceTabId: number) =>
  ({ templates }: RootState): undefined | string => {
    const { spaceList } = templates.project.data[templates.project.activeId];

    return spaceList[spaceId].spaceTabs.find(
      (spaceTab) => spaceTab.spaceTabId === spaceTabId,
    )?.spaceTabTitle;
  };

export const selectPhaseName =
  (id: number) =>
  ({ templates }: RootState): undefined | string => {
    const { projectPhases, tasks } =
      templates.project.data[templates.project.activeId];

    if (projectPhases[id]) {
      return undefined; // no parent for a phase
    }

    const getPhaseName = (_id) => {
      const { parentTask, projectPhase } = tasks[_id];

      return projectPhase
        ? projectPhases[projectPhase.projectPhaseId]?.projectPhaseName
        : parentTask
        ? getPhaseName(parentTask.taskId)
        : undefined;
    };

    return getPhaseName(id);
  };

export const selectSpaceData =
  (id: number) =>
  ({ templates }: RootState) => {
    const { spaceList } = templates.project.data[templates.project.activeId];

    if (!spaceList[id]) {
      return undefined;
    }

    return spaceList[id];
  };

export const getActiveTemplatePlaceholders =
  () =>
  ({ templates }: RootState): { [id: number]: Team } =>
    templates.project.data[templates.project.activeId].placeholders;
